import {LocationQuery, Router} from "vue-router";

/** Adds or updates a single query in the URL.
 * @param router A {@link Router} instance.
 * @param key Key of the query.
 * @param value Value of the query.
 *
 * How it looks in the URL: ?key=value
 */
export const updateQuery = async (
  router: Router,
  key: string,
  value: string
): Promise<void> => {
  await router.replace({
    ...router.currentRoute,
    query: {
      ...router.currentRoute.value.query,
      ...{
        [key]: value
      }
    }
  });
};

export const addNewQuery = async (
    router: Router,
    key: string,
    value: string
): Promise<void> => {
  const queries = router.currentRoute.value.query[key] as string[] || [];

  if (!queries.includes(value)) {
    queries.push(value);
  } else {
    const index = queries.indexOf(value);
    if (index !== -1) {
      queries.splice(index, 1);
    }
  }

  await router.replace({
    ...router.currentRoute,
    query: {
      ...router.currentRoute.value.query,
      ...{
        [key]: queries
      }
    }
  });
};

/** Adds or updates multiple queries at the same time in the URL.
 * @param router A {@link Router} instance.
 * @param queries Object containing a number of key-value query params.
 *
 * How it looks in the URL: ?key=value&key=value&...
 */
export const updateQueries = async (
  router: Router,
  queries: LocationQuery
): Promise<void> => {
  await router.replace({
    ...router.currentRoute,
    query: {
      ...router.currentRoute.value.query,
      ...queries
    }
  });
};

/** Removes a single query in the URL.
 * @param router A {@link Router} instance.
 * @param key Key of the query.
 */
export const removeQuery = async (
  router: Router,
  key: string
): Promise<void> => {
  const newQuery: LocationQuery = Object.assign(
    {},
    router.currentRoute.value.query
  );
  delete newQuery[key];
  await router.replace({
    ...router.currentRoute,
    query: newQuery
  });
};

/** Removes multiple queries in the URL.
 * @param router A {@link Router} instance.
 * @param keys The keys of the queries that will be deleted.
 */
export const removeQueries = async (
  router: Router,
  keys: string[]
): Promise<void> => {
  const newQuery: LocationQuery = Object.assign(
    {},
    router.currentRoute.value.query
  );
  for (const key of keys) {
    delete newQuery[key];
  }
  await router.replace({
    ...router.currentRoute,
    query: newQuery
  });
};

/** Removes all the queries in the URL.
 * @param router A {@link Router} instance.
 */
export const clearAllQueries = async (router: Router): Promise<void> => {
  await router.replace({
    ...router.currentRoute,
    query: {}
  });
};
